import React, { useEffect, useMemo, useState } from 'react';

import { Modal, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import type { CreateTarget } from 'actions';

import { useAppDispatch, useAppSelector } from 'redux/store';
import { createLock, updateLock } from '../../../../redux/slices/locksSlice';
import type { FetchedLock } from 'api/locks/endpoints/getLock';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { FolderSelect } from 'components/forms/FolderSelect';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

const NAME_RE = /^[A-Za-z0-9_-]+$/;

export interface EditLockModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  data: FetchedLock | null;
  // Workspace create pre-target: seeds the zone + folder on create.
  createIn?: CreateTarget;
}

interface LockFormState {
  id?: number;
  original_zone_name: string;
  original_name: string;
  original_description: string;
  zone_name: string;
  name: string;
  limit: number | '';
  description: string;
  folder_id: number | null;
}

const emptyForm: LockFormState = {
  id: undefined,
  original_zone_name: '',
  original_name: '',
  original_description: '',
  zone_name: '',
  name: '',
  limit: '',
  description: '',
  folder_id: null,
};

/**
 * Create / edit a lock. Self-contained: holds its own form state, fetches the
 * permission-filtered zone list for the dropdown, and persists via the lock
 * thunks — so it can be opened from anywhere. `original_*` carry the pre-edit
 * identity the PUT addresses (so a rename still targets the right lock).
 */
const EditLockModal: React.FC<EditLockModalProps> = ({ show, handleClose, mode, data, createIn }) => {
  const dispatch = useAppDispatch();
  const { zones } = useAppSelector((state) => state.listOfZones);

  const [form, setForm] = useState<LockFormState>(emptyForm);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show, form);

  // Initialise the form whenever the dialog opens.
  useEffect(() => {
    if (!show) return;
    if (mode === 'edit' && data) {
      setForm({
        id: data.id,
        original_zone_name: data.zone_name,
        original_name: data.name,
        original_description: data.description ?? '',
        zone_name: data.zone_name,
        name: data.name,
        limit: data.limit ?? '',
        description: data.description ?? '',
        folder_id: data.folder_id ?? null,
      });
    } else {
      setForm({ ...emptyForm, zone_name: createIn?.zoneName ?? '', folder_id: createIn?.folderId ?? null });
    }
  }, [show, mode, data, createIn]);

  // Fetch the (permission-filtered) zones for the dropdown.
  useEffect(() => {
    if (show) {
      dispatch(getAuthorizedZones({ resourceType: 'locks', action: mode === 'edit' ? 'update' : 'create' }) as any);
    }
  }, [dispatch, show, mode]);

  // Default the new lock's zone to the first available once zones load.
  useEffect(() => {
    if (show && mode === 'create' && !form.zone_name && zones?.length) {
      setForm((prev) => ({ ...prev, zone_name: zones[0].name }));
    }
  }, [show, mode, zones, form.zone_name]);

  const zonesOptions = useMemo(() => (zones || []).map((z: any) => z.name), [zones]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: name === 'limit' ? (value === '' ? '' : Number(value)) : value }));
  };

  const resetAndClose = () => {
    setForm(emptyForm);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const canSave = !!form.zone_name && !!form.name && NAME_RE.test(form.name) && form.limit !== '' && typeof form.limit === 'number' && form.limit > 0;

  const handleSave = async () => {
    const payload: {
      zone_name: string;
      name: string;
      limit: number;
      description?: string | null;
      folder_id: number | null;
    } = {
      zone_name: form.zone_name,
      name: form.name,
      limit: Number(form.limit),
      folder_id: form.folder_id ?? null,
    };

    setIsSubmitting(true);
    try {
      if (form.id) {
        const currentDescription = form.description ?? '';
        const originalDescription = form.original_description ?? '';
        if (currentDescription !== originalDescription) {
          payload.description = currentDescription || null;
        }
        const result = await dispatch(updateLock({ original_zone_name: form.original_zone_name, original_name: form.original_name, ...payload }));
        if (updateLock.rejected.match(result)) return;
      } else {
        if (form.description) {
          payload.description = form.description;
        }
        const result = await dispatch(createLock(payload));
        if (createLock.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleRequestClose}>
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Lock' : 'Create New Lock'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Zone</Form.Label>
              <Form.Select aria-label="zone-select" name="zone_name" value={form.zone_name} onChange={handleChange}>
                {zonesOptions.map((name: string, i: number) => (
                  <option key={i} value={name}>
                    {name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Folder</Form.Label>
              <FolderSelect value={form.folder_id ?? null} onChange={(folder_id) => setForm((prev) => ({ ...prev, folder_id }))} zoneName={form.zone_name} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Lock Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter lock name (letters, numbers, -, _)"
                name="name"
                value={form.name}
                onChange={handleChange}
                isInvalid={!!form.name && !NAME_RE.test(form.name)}
              />
              <Form.Control.Feedback type="invalid">Use only letters, numbers, &quot;-&quot; and &quot;_&quot; (no spaces).</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Limit</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter positive number"
                name="limit"
                min={1}
                value={form.limit === '' ? '' : form.limit}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} placeholder="Optional description" name="description" value={form.description} onChange={handleChange} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" disabled={!canSave || isSubmitting} onClick={handleSave}>
            Save and Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditLockModal;
